import { X } from "lucide-react"
import { useEffect, useState } from "react"
import type { Client } from "../types"

type AddInvoiceModalProps = {
  isOpen: boolean
  onClose: () => void
  onSave: (invoice: { clientId: string; amount: number; description: string; due_date: string }) => void | Promise<void>
}

const AddInvoiceModal = ({ isOpen, onClose, onSave }: AddInvoiceModalProps) => {
  const [clients, setClients] = useState<Client[]>([])
  const [formData, setFormData] = useState({
    clientId: "",
    amount: 0,
    description: "",
    due_date: "",
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
        fetch("/api/clients")
            .then(res => res.json())
            .then(data => {
                if (data.clients) setClients(data.clients)
            })
            .catch(console.error)
    }
  }, [isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    try {
      await onSave(formData)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-[24px] bg-white p-6 shadow-xl animate-in fade-in zoom-in-95">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-slate-900">New Invoice</h2>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-slate-100 transition">
            <X className="h-5 w-5 text-stone" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Client</label>
            <select
              required
              value={formData.clientId}
              onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
              className="w-full rounded-xl border border-line px-4 py-2.5 text-sm focus:border-moss focus:outline-none focus:ring-4 focus:ring-moss/10 bg-white"
            >
              <option value="">Select a client...</option>
              {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Amount ($)</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    amount: e.target.value ? Number.parseFloat(e.target.value) : 0,
                  })
                }
                className="w-full rounded-xl border border-line px-4 py-2.5 text-sm focus:border-moss focus:outline-none focus:ring-4 focus:ring-moss/10"
              />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <input
              type="text"
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full rounded-xl border border-line px-4 py-2.5 text-sm focus:border-moss focus:outline-none focus:ring-4 focus:ring-moss/10"
              placeholder="e.g. Q1 Retainer"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Due Date</label>
            <input
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              className="w-full rounded-xl border border-line px-4 py-2.5 text-sm focus:border-moss focus:outline-none focus:ring-4 focus:ring-moss/10"
            />
          </div>

          <p className="text-xs text-stone bg-slate-50 p-3 rounded-lg">
            This will create a draft invoice in Stripe and email it to the client immediately if an email is on file.
          </p>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-full border border-line px-4 py-2.5 text-sm font-semibold text-stone hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 rounded-full bg-moss px-4 py-2.5 text-sm font-semibold text-white shadow-soft hover:bg-moss/90 transition disabled:opacity-50"
            >
              Create & Send
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddInvoiceModal
